import { type ReactElement, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, Snackbar } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { DecisionLoadingCard } from "../components/cards/DecisionLoadingCard"
import { KonanPageHeader } from "../components/core/KonanPageHeader"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanPagination as Pagination } from "../components/tables/KonanPagination"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { CopyComponentsFlow } from "../features/ImportFlow/CopyComponentsFlow"
import { RulesetCard } from "../features/Ruleset/components/RulesetCard"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { PaginatedRuleSetListList } from "../types/generated/api/PaginatedRuleSetListList"
import { useIsPermitted } from "../utils/PermissionsHelpers"

/**
 * Ruleset page component - displays all rulesets in a project
 * @returns {ReactElement} The ruleset page component
 */
export function RulesetPage(): ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  //hooks
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve ruleset", permissions.konan, flattenedKonanPermissions)
  const canAddRuleset = useIsPermitted("Add ruleset", permissions.konan, flattenedKonanPermissions)

  // Pagination
  const pageSize = 3
  const [page, setPage] = useState<number>(0)

  const [duplicatedId, setDuplicatedId] = useState<string | null>(null)

  const [showRulesetCard, setShowRulesetCard] = useState<boolean>(false)

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { t } = useTranslation("rulesets")

  //SSF States
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<SortKey>("mostRecent")

  // SSF Types And Constants
  type SortKey = keyof typeof SORT_OPTIONS
  const SORT_OPTIONS = {
    mostRecent: "-created_at",
    lastUsed: "-last_used",
  } as const

  const duplicateRuleset = (uuid: string): void => {
    setDuplicatedId(uuid)
    // scroll to top
    window.scrollTo(0, 0)
  }

  // fetch rulesets
  const { isLoading: isRulesetsLoading, data: rulesets } = useQuery<
    AxiosResponse<PaginatedRuleSetListList>,
    AxiosError
  >(
    ["rulesets", projectId, page, pageSize, searchValue, sortValue],
    () => KonanAPI.fetchRulesets(projectId as string, page + 1, pageSize, searchValue, SORT_OPTIONS[sortValue]),
    {
      enabled: !!projectId,
    },
  )

  return (
    <Grid item container spacing={2}>
      <CopyComponentsFlow<"Ruleset">
        key={isDialogOpen.toString()}
        componentName="Ruleset"
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
      />

      <Grid item xs={12} textAlign={"right"}>
        <KonanPageHeader title={t("title")} />
        {/* SSF */}
        <Grid mt={2}>
          <KonanSubHeader
            sortValue={t(sortValue)}
            setSortValue={(value) => {
              setPage(() => 0)
              setSortValue(value as SortKey)
            }}
            sortOptions={[
              { label: t("mostRecent"), value: "mostRecent" },
              { label: t("lastUsed"), value: "lastUsed" },
            ]}
            searchValue={searchValue}
            containerType="Ruleset"
            setSearchValue={(value) => {
              setPage(() => 0)
              setSearchValue(() => value as string)
            }}
            isDebouncedInput
            actions={
              canShowDetails && canAddRuleset
                ? [
                    <Button
                      key="Import"
                      variant="secondary"
                      onClick={setIsDialogOpen.bind(null, true)}
                      disabled={showRulesetCard || isRulesetsLoading || !!duplicatedId}
                      size="regular"
                    >
                      {t("importRulesetBtn")}
                    </Button>,
                    <Button
                      variant="primary"
                      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                      onClick={() => setShowRulesetCard(true)}
                      disabled={showRulesetCard || isRulesetsLoading || !!duplicatedId}
                      size="regular"
                      key={"create-ruleset-button"}
                    >
                      {t("createRulesetBtn")}
                    </Button>,
                  ]
                : []
            }
          />
        </Grid>
      </Grid>

      {!canShowDetails && (
        <Grid item flex={1}>
          <Snackbar
            fullWidth
            severity="info"
            title={t("cannotShowDetailsSnackBar.title")}
            description={t("cannotShowDetailsSnackBar.description")}
          />
        </Grid>
      )}

      {/* Ruleset searching empty state */}
      {!!projectId && !isRulesetsLoading && !rulesets?.data.count && !showRulesetCard && searchValue && (
        <KonanEmptyState
          title={t("searchEmptyState.title", { value: searchValue })}
          subTitle={t("searchEmptyState.subtitle")}
        />
      )}

      {/* Ruleset empty state */}
      {!!projectId && !isRulesetsLoading && !rulesets?.data.count && !showRulesetCard && !searchValue && (
        <Grid item xs={12} className={"empty-container"}>
          <KonanEmptyState
            title={t("emptyState.title")}
            subTitle={t("emptyState.subtitle")}
            buttonText={t("emptyState.createRulesetBtn")}
            setAction={setShowRulesetCard}
          />
        </Grid>
      )}

      {!isRulesetsLoading && (!!duplicatedId || showRulesetCard) && (
        <Grid item xs={12} key={!!duplicatedId ? `duplicate-${duplicatedId}` : "new"}>
          <RulesetCard
            uuid={duplicatedId ?? "new"}
            isDuplicate={!!duplicatedId}
            closeCreateCard={!!duplicatedId ? () => setDuplicatedId(null) : () => setShowRulesetCard(false)}
          />
        </Grid>
      )}

      {/* Loading state */}
      {isRulesetsLoading
        ? [1, 2].map((item: number) => <DecisionLoadingCard key={item} />)
        : rulesets?.data?.results?.map((ruleset) => (
            <Grid item xs={12} key={ruleset.uuid}>
              <RulesetCard
                duplicateRuleset={duplicateRuleset}
                uuid={ruleset.uuid}
                metaData={ruleset}
                disableDuplicateButton={!!duplicatedId || showRulesetCard}
                canShowDetails={canShowDetails}
              />
            </Grid>
          ))}

      {/* Pagination Section */}
      {!isRulesetsLoading && rulesets?.data.results && rulesets?.data.results.length > 0 && (
        <Grid item>
          <Pagination
            page={page}
            count={rulesets.data.count as number}
            onPageChange={(newPage: number) => {
              setPage(newPage)
              window.scrollTo(0, 0)
            }}
            rowsPerPage={pageSize}
            variant={"complex"}
            hideRowCount={true}
          />
        </Grid>
      )}
    </Grid>
  )
}
